/**
 * drillLabel — builds the human-readable label shown in a drill breadcrumb
 * (DrillBreadcrumbs.tsx) when the user focuses into a subquery. Each drill
 * "kind" maps to a SQL keyword; when the caller can hand us the source-clause
 * identifier (relation name / alias / column ref) we render "<KEYWORD>
 * <identifier>" (e.g. "FROM users", "SELECT user_id"). When no identifier is
 * available — an un-aliased derived table, an unparsable fragment — we fall
 * back to the POSITIONAL label the call sites used before this slice
 * ("FROM #1", "SELECT col 1", "JOIN #1").
 *
 * The identifier is sanitized (internal whitespace collapsed, newlines
 * stripped) and truncated to ≤24 chars, appending a single ellipsis when cut.
 * Quoting is preserved — a quoted identifier the caller passes through (e.g.
 * `"Order Items"`) keeps its quotes. The caller owns extraction; this builder
 * only formats what it is given.
 */

export type DrillLabelKind =
  | 'from'
  | 'join'
  | 'select-col'
  | 'where'
  | 'having'
  | 'cte';

export interface BuildDrillLabelArgs {
  readonly kind: DrillLabelKind;
  /** Extracted source identifier (relation/alias/column). Null → positional. */
  readonly identifier: string | null | undefined;
  /** 0-based index of the item within its clause; the positional label is index+1. */
  readonly positionalIndex: number;
}

const KEYWORD: Record<DrillLabelKind, string> = {
  from: 'FROM',
  join: 'JOIN',
  'select-col': 'SELECT',
  where: 'WHERE',
  having: 'HAVING',
  cte: 'CTE',
};

const MAX_IDENTIFIER_LENGTH = 24;

/**
 * Collapse internal runs of whitespace (incl. newlines/tabs) to a single space
 * and trim the ends. Returns '' for input that is null/empty/whitespace-only.
 *
 * Exported so callers that build their own label shape around an identifier
 * (rather than going through `buildDrillLabel` itself) — e.g. `CompactCteStrip`'s
 * `<name> (CTE)` breadcrumb — still apply the same sanitization.
 */
export function sanitizeIdentifier(raw: string | null | undefined): string {
  if (!raw) return '';
  return raw.replace(/\s+/g, ' ').trim();
}

/**
 * Truncate to MAX_IDENTIFIER_LENGTH chars, appending a single ellipsis when cut.
 * Exported alongside `sanitizeIdentifier` for the same reason — see above.
 */
export function truncateIdentifier(value: string): string {
  if (value.length <= MAX_IDENTIFIER_LENGTH) return value;
  return `${value.slice(0, MAX_IDENTIFIER_LENGTH)}…`;
}

/**
 * Positional fallback label, matching the strings the call sites emitted before
 * this slice: SELECT columns read "SELECT col N", everything else "<KEYWORD> #N".
 */
function positionalLabel(kind: DrillLabelKind, positionalIndex: number): string {
  const n = positionalIndex + 1;
  if (kind === 'select-col') return `${KEYWORD[kind]} col ${n}`;
  return `${KEYWORD[kind]} #${n}`;
}

export function buildDrillLabel({
  kind,
  identifier,
  positionalIndex,
}: BuildDrillLabelArgs): string {
  const sanitized = sanitizeIdentifier(identifier);
  if (sanitized.length === 0) return positionalLabel(kind, positionalIndex);
  return `${KEYWORD[kind]} ${truncateIdentifier(sanitized)}`;
}

/**
 * Extract the leading identifier from a raw FROM / JOIN clause fragment: the
 * relation name, or its alias when one is present (callers prefer the alias).
 * Returns null for a derived table (`( … )`) with no alias, or any fragment
 * whose leading token isn't identifier-shaped.
 *
 * Quoting is preserved (a `"Quoted"` / `[Bracketed]` / `` `Backtick` `` token
 * is returned verbatim). Schema/db qualifiers are kept as-is (e.g. `dbo.users`).
 */
export function extractRelationIdentifier(rawText: string | null | undefined): string | null {
  const text = (rawText ?? '').trim();
  if (text.length === 0) return null;
  // Derived table with no alias → starts with an opening paren and never names
  // anything; let the caller fall back to positional.
  if (text.startsWith('(')) {
    const afterParen = afterBalancedParen(text);
    if (afterParen === null) return null;
    const tail = text.slice(afterParen).trim();
    const aliasTok = leadingToken(stripAs(tail));
    return aliasTok;
  }
  const first = leadingToken(text);
  if (first === null) return null;
  const rest = text.slice(first.length).trim();
  const aliasTok = leadingToken(stripAs(rest));
  return aliasTok ?? first;
}

/**
 * Extract the leading column reference from a WHERE / HAVING predicate fragment
 * (e.g. "status = 'open'" → "status", "t.total > 0" → "t.total"). Returns null
 * when the fragment opens with a paren group or a non-identifier token.
 */
export function extractPredicateColumn(rawText: string | null | undefined): string | null {
  const text = (rawText ?? '').trim();
  if (text.length === 0 || text.startsWith('(')) return null;
  return leadingToken(text);
}

/** Strip a leading `AS` keyword (case-insensitive) from an alias tail. */
function stripAs(tail: string): string {
  const m = /^as\s+/i.exec(tail);
  return m ? tail.slice(m[0].length) : tail;
}

/** Index just past the balanced opening paren group, or null if unbalanced. */
function afterBalancedParen(text: string): number | null {
  let depth = 0;
  for (let i = 0; i < text.length; i++) {
    const c = text[i];
    if (c === '(') depth++;
    else if (c === ')') {
      depth--;
      if (depth === 0) return i + 1;
    }
  }
  return null;
}

/**
 * Leading identifier token: a dotted-path identifier (`schema.table`), or a
 * single quoted/bracketed/backtick token. Returns null when the first
 * non-space char isn't identifier-shaped.
 */
function leadingToken(text: string): string | null {
  const t = text.trimStart();
  if (t.length === 0) return null;
  const m = /^(?:[A-Za-z_][\w$]*|"[^"]*"|\[[^\]]*\]|`[^`]*`)(?:\.(?:[A-Za-z_][\w$]*|"[^"]*"|\[[^\]]*\]|`[^`]*`))*/.exec(t);
  return m ? m[0] : null;
}
